import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  clearGanttTimelinePresence,
  cursorColorForUid,
  GANTT_TIMELINE_CURSOR_THROTTLE_MS,
  GANTT_TIMELINE_OUTLINE_SYNC_THROTTLE_MS,
  GANTT_TIMELINE_VIEWPORT_THROTTLE_MS,
  ganttTimelineRoomId,
  isFreshGanttTimelinePeer,
  joinGanttTimelineSync,
  leaveGanttTimelineSync,
  publishGanttTimelinePresence,
  publishGanttTimelineSyncOutline,
  publishGanttTimelineSyncViewport,
  subscribeGanttTimelinePresence,
  subscribeGanttTimelineSync,
  type GanttTimelinePeerPresence,
} from '../utils/ganttTimelinePresenceService';
import {
  applyGanttSyncedOutline,
  buildGanttSyncedOutlineAll,
  buildGanttSyncedOutlineSingle,
  type GanttGanttOutlineSlice,
  type GanttSyncedOutline,
  type GanttSyncedViewport,
} from '../utils/ganttTimelineViewSync';
import type { GroupTimelineColorMode } from '../utils/groupTimeline';
import type { GroupTimelineDetailFilter } from '../utils/groupTimelineFilter';
import type { GroupTimelineExtraTasksByFamily, GroupTimelineFamily, GroupTimelineLayoutMode, GroupTimelineSortMode } from '../utils/groupTimeline';
import type { StructureFilterValue } from '../utils/ganttSubtaskRows';
import type { GanttAllProjectsApi } from '../components/pm/GanttTimeline';

function randomSyncId(): string {
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

export type GanttTimelineCollaborationMode = {
  kind: 'single';
  projectId: string;
  structureFilter: Set<StructureFilterValue> | null;
} | {
  kind: 'all';
  family: GroupTimelineFamily;
  colorMode: GroupTimelineColorMode;
  layout: GroupTimelineLayoutMode;
  sort: GroupTimelineSortMode;
  filter: GroupTimelineDetailFilter;
  extraTasks: GroupTimelineExtraTasksByFamily;
};

export type UseGanttTimelineCollaborationParams = {
  enabled: boolean;
  user: { id: string; name?: string | null; email?: string | null } | null;
  mode: GanttTimelineCollaborationMode;
  ganttOutlineApiRef: React.MutableRefObject<GanttAllProjectsApi | null>;
  setStructureFilter?: (next: Set<StructureFilterValue> | null) => void;
  setGroupTimelineFamily?: (family: GroupTimelineFamily) => void;
  setGroupTimelineColorMode?: (mode: GroupTimelineColorMode) => void;
  setGroupTimelineLayout?: (mode: GroupTimelineLayoutMode) => void;
  setGroupTimelineSort?: (mode: GroupTimelineSortMode) => void;
  setGroupTimelineFilter?: (filter: GroupTimelineDetailFilter) => void;
  setGroupTimelineExtraTasks?: (map: GroupTimelineExtraTasksByFamily) => void;
  filterUiBusyRef?: React.MutableRefObject<boolean>;
  onSynced?: (peerName: string) => void;
};

export function useGanttTimelineCollaboration(params: UseGanttTimelineCollaborationParams) {
  const {
    enabled,
    user,
    mode,
    ganttOutlineApiRef,
    setStructureFilter,
    setGroupTimelineFamily,
    setGroupTimelineColorMode,
    setGroupTimelineLayout,
    setGroupTimelineSort,
    setGroupTimelineFilter,
    setGroupTimelineExtraTasks,
    filterUiBusyRef,
    onSynced,
  } = params;

  const [peers, setPeers] = useState<GanttTimelinePeerPresence[]>([]);
  const [syncId, setSyncId] = useState<string | null>(null);
  const ganttOutlineRef = useRef<GanttGanttOutlineSlice | null>(null);
  const lastPresencePublishRef = useRef(0);
  const lastOutlinePublishRef = useRef(0);
  const lastViewportPublishRef = useRef(0);
  const applyingRemoteOutlineRef = useRef(false);
  const applyingRemoteViewportRef = useRef(false);
  const pendingViewportRef = useRef<GanttSyncedViewport | null>(null);
  const viewportTimerRef = useRef<number | null>(null);
  const lastAppliedOutlineHashRef = useRef<string | null>(null);
  const lastAppliedViewportKeyRef = useRef<string | null>(null);
  const syncIdRef = useRef<string | null>(null);
  syncIdRef.current = syncId;

  const roomId = useMemo(() => {
    if (!enabled) return '';
    if (mode.kind === 'single') {
      return ganttTimelineRoomId({ mode: 'single', projectId: mode.projectId });
    }
    return ganttTimelineRoomId({ mode: 'all', family: mode.family });
  }, [enabled, mode]);

  const buildOutline = useCallback((): GanttSyncedOutline | null => {
    const ganttApi = ganttOutlineApiRef.current;
    const gantt = ganttApi?.getOutlineState() ?? ganttOutlineRef.current;
    if (!gantt) return null;
    if (mode.kind === 'single') {
      return buildGanttSyncedOutlineSingle({
        structureFilter: mode.structureFilter,
        gantt,
      });
    }
    return buildGanttSyncedOutlineAll({
      family: mode.family,
      colorMode: mode.colorMode,
      layout: mode.layout,
      sort: mode.sort,
      filter: mode.filter,
      extraTasks: mode.extraTasks,
      gantt,
    });
  }, [ganttOutlineApiRef, mode]);

  const selfOutline = useMemo(() => buildOutline(), [buildOutline]);
  const selfViewHash = selfOutline?.viewHash ?? '';

  const applyOutlineHandlers = useMemo(
    () => ({
      setStructureFilter,
      setGroupTimelineFamily,
      setGroupTimelineColorMode,
      setGroupTimelineLayout,
      setGroupTimelineSort,
      setGroupTimelineFilter,
      setGroupTimelineExtraTasks,
      applyGanttOutline: (gantt: GanttGanttOutlineSlice) => {
        ganttOutlineApiRef.current?.applyOutlineState(gantt);
      },
    }),
    [
      setStructureFilter,
      setGroupTimelineFamily,
      setGroupTimelineColorMode,
      setGroupTimelineLayout,
      setGroupTimelineSort,
      setGroupTimelineFilter,
      setGroupTimelineExtraTasks,
      ganttOutlineApiRef,
    ],
  );

  const applyRemoteOutline = useCallback(
    (outline: GanttSyncedOutline) => {
      if (filterUiBusyRef?.current) return;
      applyingRemoteOutlineRef.current = true;
      try {
        applyGanttSyncedOutline(outline, applyOutlineHandlers);
      } finally {
        window.setTimeout(() => {
          applyingRemoteOutlineRef.current = false;
        }, 0);
      }
    },
    [applyOutlineHandlers, filterUiBusyRef],
  );

  useEffect(() => {
    if (!enabled || !user?.id || !roomId) {
      setPeers([]);
      return;
    }
    return subscribeGanttTimelinePresence(roomId, user.id, setPeers);
  }, [enabled, user?.id, roomId]);

  useEffect(() => {
    if (!enabled || !user?.id || !roomId || !selfOutline) return;
    const now = Date.now();
    if (now - lastPresencePublishRef.current < GANTT_TIMELINE_CURSOR_THROTTLE_MS) return;
    lastPresencePublishRef.current = now;
    const color = cursorColorForUid(user.id);
    void publishGanttTimelinePresence(roomId, {
      uid: user.id,
      name: user.name || user.email || 'Someone',
      color,
      viewHash: selfOutline.viewHash,
      syncId,
      outline: selfOutline,
    });
  }, [enabled, user, roomId, selfOutline, syncId]);

  useEffect(() => {
    if (!enabled || !user?.id || !roomId) return;
    return () => {
      void clearGanttTimelinePresence(roomId, user.id);
    };
  }, [enabled, user?.id, roomId]);

  const applyRemoteViewport = useCallback(
    (viewport: GanttSyncedViewport) => {
      const key = `${viewport.zoom}|${Math.round(viewport.anchorMs)}|${Math.round(viewport.scrollTop)}`;
      if (key === lastAppliedViewportKeyRef.current) return;
      lastAppliedViewportKeyRef.current = key;
      applyingRemoteViewportRef.current = true;
      ganttOutlineApiRef.current?.applyViewport(viewport);
      window.setTimeout(() => {
        applyingRemoteViewportRef.current = false;
      }, 180);
    },
    [ganttOutlineApiRef],
  );

  useEffect(() => {
    if (!enabled || !user?.id || !roomId || !syncId) return;
    return subscribeGanttTimelineSync(roomId, syncId, (doc) => {
      if (!doc || doc.updatedBy === user.id) return;
      if (!doc.memberUids.includes(user.id)) return;
      if (doc.outline.viewHash !== lastAppliedOutlineHashRef.current) {
        lastAppliedOutlineHashRef.current = doc.outline.viewHash;
        applyRemoteOutline(doc.outline);
      }
      if (doc.viewport) applyRemoteViewport(doc.viewport);
    });
  }, [enabled, user?.id, roomId, syncId, applyRemoteOutline, applyRemoteViewport]);

  const publishOutlineToSync = useCallback(
    (outline: GanttSyncedOutline) => {
      const sid = syncIdRef.current;
      if (!user?.id || !roomId || !sid || applyingRemoteOutlineRef.current) return;
      const now = Date.now();
      if (now - lastOutlinePublishRef.current < GANTT_TIMELINE_OUTLINE_SYNC_THROTTLE_MS) return;
      lastOutlinePublishRef.current = now;
      void publishGanttTimelineSyncOutline({
        roomId,
        syncId: sid,
        uid: user.id,
        outline,
      });
    },
    [user?.id, roomId],
  );

  const onGanttOutlineChange = useCallback(
    (slice: GanttGanttOutlineSlice) => {
      ganttOutlineRef.current = slice;
      if (!syncIdRef.current || applyingRemoteOutlineRef.current) return;
      const outline = buildOutline();
      if (outline) publishOutlineToSync(outline);
    },
    [buildOutline, publishOutlineToSync],
  );

  useEffect(() => {
    if (!syncIdRef.current || applyingRemoteOutlineRef.current) return;
    const outline = buildOutline();
    if (outline) publishOutlineToSync(outline);
  }, [
    mode,
    publishOutlineToSync,
    buildOutline,
  ]);

  const freshPeers = useMemo(() => {
    const now = Date.now();
    return peers.filter((p) => isFreshGanttTimelinePeer(p, now));
  }, [peers]);

  const syncWithPeer = useCallback(
    async (peerUid: string) => {
      if (!user?.id || !roomId) return;
      const peer = freshPeers.find((p) => p.uid === peerUid);
      const peerOutline = peer?.outline;
      if (!peerOutline) return;
      const nextSyncId = peer.syncId || randomSyncId();
      setSyncId(nextSyncId);
      lastAppliedOutlineHashRef.current = peerOutline.viewHash;
      applyRemoteOutline(peerOutline);
      if (peer.viewport) applyRemoteViewport(peer.viewport);
      await joinGanttTimelineSync({
        roomId,
        syncId: nextSyncId,
        uid: user.id,
        outline: peerOutline,
      });
      onSynced?.(peer.name);
    },
    [user?.id, roomId, freshPeers, applyRemoteOutline, applyRemoteViewport, onSynced],
  );

  const flushViewport = useCallback(
    (viewport: GanttSyncedViewport) => {
      if (!user?.id || !roomId || applyingRemoteViewportRef.current) return;
      lastViewportPublishRef.current = Date.now();
      const sid = syncIdRef.current;
      if (sid) {
        void publishGanttTimelineSyncViewport({
          roomId,
          syncId: sid,
          uid: user.id,
          viewport,
        });
        return;
      }
      void publishGanttTimelinePresence(roomId, {
        uid: user.id,
        name: user.name || user.email || 'Someone',
        color: cursorColorForUid(user.id),
        viewHash: selfViewHash,
        syncId: null,
        viewport,
      });
    },
    [user, roomId, selfViewHash],
  );

  const onViewportChange = useCallback(
    (viewport: GanttSyncedViewport) => {
      if (!enabled || applyingRemoteViewportRef.current) return;
      const key = `${viewport.zoom}|${Math.round(viewport.anchorMs)}|${Math.round(viewport.scrollTop)}`;
      if (key === lastAppliedViewportKeyRef.current) return;
      const now = Date.now();
      if (now - lastViewportPublishRef.current < GANTT_TIMELINE_VIEWPORT_THROTTLE_MS) {
        pendingViewportRef.current = viewport;
        if (viewportTimerRef.current == null) {
          viewportTimerRef.current = window.setTimeout(() => {
            viewportTimerRef.current = null;
            const pending = pendingViewportRef.current;
            pendingViewportRef.current = null;
            if (pending) flushViewport(pending);
          }, GANTT_TIMELINE_VIEWPORT_THROTTLE_MS);
        }
        return;
      }
      pendingViewportRef.current = null;
      flushViewport(viewport);
    },
    [enabled, flushViewport],
  );

  useEffect(() => {
    return () => {
      if (viewportTimerRef.current != null) window.clearTimeout(viewportTimerRef.current);
    };
  }, []);

  const leaveSync = useCallback(async () => {
    if (!user?.id || !roomId || !syncId) {
      setSyncId(null);
      return;
    }
    await leaveGanttTimelineSync({ roomId, syncId, uid: user.id });
    setSyncId(null);
  }, [user?.id, roomId, syncId]);

  const onCursorSample = useCallback(
    (sample: {
      rowKey: string | null;
      timeMs: number | null;
      pane: 'chart' | 'left';
    }) => {
      if (!enabled || !user?.id || !roomId || !selfOutline) return;
      const now = Date.now();
      if (now - lastPresencePublishRef.current < GANTT_TIMELINE_CURSOR_THROTTLE_MS) return;
      lastPresencePublishRef.current = now;
      void publishGanttTimelinePresence(roomId, {
        uid: user.id,
        name: user.name || user.email || 'Someone',
        color: cursorColorForUid(user.id),
        viewHash: selfOutline.viewHash,
        syncId,
        outline: selfOutline,
        rowKey: sample.rowKey,
        timeMs: sample.timeMs,
        pane: sample.pane,
      });
    },
    [enabled, user, roomId, selfOutline, syncId],
  );

  const onCursorClear = useCallback(() => {
    if (!enabled || !user?.id || !roomId || !selfOutline) return;
    void publishGanttTimelinePresence(roomId, {
      uid: user.id,
      name: user.name || user.email || 'Someone',
      color: cursorColorForUid(user.id),
      viewHash: selfOutline.viewHash,
      syncId,
      outline: selfOutline,
      rowKey: null,
      timeMs: null,
    });
  }, [enabled, user, roomId, selfOutline, syncId]);

  const ganttTimelinePresence = useMemo(() => {
    if (!enabled || !user?.id || !selfViewHash) return undefined;
    return {
      selfViewHash,
      selfSyncId: syncId,
      peers: freshPeers,
      onCursorSample,
      onCursorClear,
    };
  }, [enabled, user?.id, selfViewHash, syncId, freshPeers, onCursorSample, onCursorClear]);

  const viewHashMatchedPeerCount = useMemo(
    () => freshPeers.filter((p) => p.viewHash === selfViewHash).length,
    [freshPeers, selfViewHash],
  );

  return {
    freshPeers,
    syncId,
    syncWithPeer,
    leaveSync,
    onGanttOutlineChange,
    onViewportChange,
    ganttTimelinePresence,
    selfViewHash,
    viewHashMatchedPeerCount,
    showCollaborationUi: enabled && freshPeers.length > 0,
  };
}
